import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function LockingPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 top-24">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_45cdbc01a6736310c4e4978b82c9d127_66ce684c.jpg"
            alt="Apple Wine"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-8">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            ロッキングとは何か？
          </h1>
          <p className="text-2xl md:text-3xl text-white/95 mb-12 leading-relaxed">
            1杯の特別なワインから学ぶ、未来の価値の育て方。
          </p>

          {/* YouTube Video */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-3xl shadow-2xl"
                src="https://www.youtube.com/embed/HLjrwUGg04o"
                title="Mawari Locking Explanation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              className="px-10 py-5 bg-white text-pink-600 text-xl rounded-full font-bold hover:shadow-2xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              ロッキングの仕組みを見る
            </button>
            <button
              className="px-10 py-5 bg-white/20 backdrop-blur-sm text-white text-xl rounded-full font-bold hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer border-2 border-white"
            >
              報酬の種類を見る
            </button>
          </div>
        </div>
      </section>

      {/* Section 1: 物語で理解するロッキング */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-16 text-center">
            特別なワインの物語
          </h2>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-12 md:p-16 border border-pink-100">
            <div className="space-y-10 text-xl md:text-2xl text-gray-800 leading-relaxed">
              <p>
                あなたが、とても希少で特別なワインのオーナーになったと想像してください。
              </p>

              <p>
                今すぐ飲んでも美味しい。でも10年寝かせると、さらに価値が高まる可能性を秘めています。
              </p>

              <div className="bg-white rounded-2xl p-8 my-8">
                <p className="text-red-600 font-bold mb-4">
                  もし、みんなが今すぐ売ってしまったら？
                </p>
                <p>
                  市場に溢れ、価値は下がります。
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 my-8">
                <p className="text-green-600 font-bold mb-4">
                  でも、ほとんどの人が協力して待てば？
                </p>
                <p>
                  市場に出回る本数は減り、希少価値が高まり、自然と価値は育っていきます。
                </p>
              </div>

              <p className="text-2xl md:text-3xl text-pink-600 font-bold text-center pt-8">
                この「みんなで価値を育てる選択」こそがロッキングです。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: ロッキングとは？ */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-8 text-center">
            ロッキングは我慢ではない
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 leading-relaxed">
            ロッキングはただ保有することではありません。ネットワークの安定に貢献し、長期的な価値を育てる積極的な参加です。
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-6">
                <i className="ri-plant-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">価値を育てる行動</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                時間をかけて価値を成長させる
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ネットワークを安定させる選択</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                みんなで協力して基盤を強くする
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">未来に参加する仕組み</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                次世代のインフラを支える
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: MAWARIに置き換えると */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-16 text-center">
            なぜ安定が重要なのか？
          </h2>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-12 md:p-16 border border-pink-100">
            <p className="text-xl md:text-2xl text-gray-800 leading-relaxed mb-12">
              MAWARIは未来の3Dインターネットの土台をつくっています。その世界がスムーズに動くためには、安定したネットワークが必要です。
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-pink-100 flex items-center justify-center">
                  <i className="ri-line-chart-line text-3xl text-pink-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">流通量が安定する</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    急激な価格変動を防ぎます
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-pink-100 flex items-center justify-center">
                  <i className="ri-global-line text-3xl text-pink-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">ネットワークが強くなる</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    より多くのノードが安定稼働します
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-pink-100 flex items-center justify-center">
                  <i className="ri-treasure-map-line text-3xl text-pink-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">長期的価値が守られる</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    プロジェクトの持続的成長を支えます
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: 3つの報酬 */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-16 text-center">
            3つの報酬
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* ノード報酬 */}
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto mb-6">
                <i className="ri-server-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">ノード報酬</h3>
              <p className="text-lg text-gray-600 leading-relaxed text-center">
                ネットワークを支える<br />
                報酬
              </p>
            </div>

            {/* ロッキング報酬 */}
            <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-6">
                <i className="ri-lock-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">ロッキング報酬</h3>
              <p className="text-lg text-gray-600 leading-relaxed text-center">
                価値を育てる<br />
                協力への感謝
              </p>
            </div>

            {/* アクティベーション報酬 */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl p-10 shadow-xl border-2 border-yellow-300 relative">
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                <i className="ri-star-fill text-2xl text-white"></i>
              </div>
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-6">
                <i className="ri-medal-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">アクティベーション<br />報酬</h3>
              <p className="text-lg text-gray-700 leading-relaxed text-center font-semibold">
                初期協力者への<br />
                特別報酬
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: 70%ルール */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-16 text-center">
            アクティベーション報酬の条件
          </h2>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl p-12 md:p-16 border-2 border-yellow-200">
            <div className="text-center mb-12">
              <div className="inline-block">
                <div className="text-9xl md:text-[12rem] font-bold text-transparent bg-clip-text bg-gradient-to-br from-yellow-500 to-orange-600 leading-none">
                  70%
                </div>
              </div>
            </div>

            <div className="space-y-8 text-xl md:text-2xl text-gray-800 leading-relaxed">
              <p className="text-center">
                毎月得られる報酬のうち<strong className="text-orange-600">70%以上をロックする。</strong>
              </p>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-center">
                  これにより翌月も<strong className="text-pink-600">アクティベーション報酬</strong>を受け取ることができます。
                </p>
              </div>

              <div className="bg-gradient-to-r from-pink-100 to-orange-100 rounded-2xl p-8 border border-orange-200">
                <p className="text-center font-bold text-orange-700">
                  この特別な制度は最初の12ヶ月間。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: あなたの選択が未来をつくる */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-16 text-center">
            ロッキングは未来への参加
          </h2>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-12 md:p-16 border border-pink-100">
            <div className="flex items-center justify-center mb-12">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                <i className="ri-hand-heart-line text-6xl text-white"></i>
              </div>
            </div>

            <div className="space-y-10 text-xl md:text-2xl text-gray-800 leading-relaxed text-center">
              <p>
                ロッキングは耐えることではありません。
              </p>

              <p>
                ワインを熟成させるように、未来の価値をみんなで育てる創造的な行動です。
              </p>

              <p className="text-2xl md:text-3xl text-pink-600 font-bold pt-8">
                あなたの選択が、ネットワークの安定と価値に直接つながっています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
            あなたは、価値を育てますか？
          </h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mt-12">
            <button
              onClick={() => handleNavigation('/locking-rewards')}
              className="px-12 py-5 bg-white text-pink-600 text-xl rounded-full font-bold hover:shadow-2xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              今すぐロッキングを学ぶ
            </button>
            <button
              onClick={() => handleNavigation('/reward-claim-manual')}
              className="px-12 py-5 bg-white/20 backdrop-blur-sm text-white text-xl rounded-full font-bold hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer border-2 border-white"
            >
              報酬の詳細を見る
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}